import React from "react";
import {
  FaBus,
  FaShieldAlt,
  FaUserCheck,
  FaCheckCircle,
  FaFirstAid,
  FaUserShield,
  FaCamera,
} from "react-icons/fa";
import TransportImage from '../../assets/images/home/transport.webp'

const TransportSection = () => {
  const transportFeatures = [
    {
      icon: <FaBus className="w-6 h-6" />,
      title: "Modern Fleet",
      desc: "Well-maintained, air-conditioned buses with comfortable seating",
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: <FaShieldAlt className="w-6 h-6" />,
      title: "GPS Tracking",
      desc: "Real-time location tracking with parental app access",
      color: "bg-emerald-100 text-emerald-600",
    },
    {
      icon: <FaUserCheck className="w-6 h-6" />,
      title: "Trained Attendants",
      desc: "Female attendants ensuring safety of younger students",
      color: "bg-purple-100 text-purple-600",
    },
    {
      icon: <FaCamera className="w-6 h-6" />,
      title: "CCTV Surveillance",
      desc: "24/7 monitoring with live feed access for parents",
      color: "bg-amber-100 text-amber-600",
    },
    {
      icon: <FaFirstAid className="w-6 h-6" />,
      title: "Medical Safety",
      desc: "First-aid kits and trained staff for emergencies",
      color: "bg-red-100 text-red-600",
    },
    {
      icon: <FaUserShield className="w-6 h-6" />,
      title: "Verified Drivers",
      desc: "Background checked, experienced drivers with training",
      color: "bg-indigo-100 text-indigo-600",
    },
  ];

  const transportStats = [
    { label: "Modern Buses", value: "12", icon: "🚌" },
    { label: "Safety Record", value: "100%", icon: "🛡️" },
    { label: "Service Hours", value: "7AM-5PM", icon: "⏰" },
    { label: "City Coverage", value: "Wide", icon: "🗺️" },
  ];

  return (
    <section className="py-12 bg-white">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto mb-10 text-center">
          <div className="inline-flex items-center px-3 py-1 mb-3 text-xs font-medium text-blue-600 rounded-full bg-blue-50">
            <FaBus className="w-3 h-3 mr-1.5" />
            Transport Service
          </div>
          <h2 className="mb-3 text-2xl font-bold text-gray-900">
            Safe & Reliable School Transport
          </h2>
          <p className="text-sm text-gray-600">
            Ensuring safe and comfortable transportation for every student
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Left Column - Large Image with Overlay Info */}
          <div className="relative">
            <div className="overflow-hidden shadow-xl rounded-2xl">
              <img
                src={TransportImage}
                alt="Modern School Bus Transport"
                className="object-cover w-full h-96"
              />
            </div>
            
            {/* Stats Overlay */}
            <div className="absolute bottom-4 left-4 right-4">
              <div className="p-4 border bg-white/90 backdrop-blur-sm rounded-xl border-white/20">
                <div className="grid grid-cols-4 gap-3">
                  {transportStats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="mb-1 text-2xl">{stat.icon}</div>
                      <div className="text-lg font-bold text-gray-900">{stat.value}</div>
                      <div className="text-xs text-gray-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Features Grid */}
          <div>
            <div className="p-5 mb-6 border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Transport Features
              </h3>
              <p className="mb-4 text-sm text-gray-600">
                Our transport service ensures maximum safety, comfort, and reliability 
                with state-of-the-art facilities and trained personnel.
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                {transportFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className="p-3 bg-white border border-gray-100 rounded-lg"
                  >
                    <div className="flex items-start gap-2">
                      <div className={`p-2 rounded-md ${feature.color}`}>
                        {feature.icon}
                      </div>
                      <div>
                        <h4 className="mb-1 text-sm font-semibold text-gray-900">
                          {feature.title}
                        </h4>
                        <p className="text-xs leading-relaxed text-gray-600">
                          {feature.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Safety Assurance */}
            <div className="p-5 border bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border-emerald-100">
              <div className="flex items-center gap-2 mb-4">
                <FaShieldAlt className="w-5 h-5 text-emerald-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Safety Assurance
                </h3>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700">Speed governors</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700">Regular maintenance</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700">First aid equipped</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700">Parent notification</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransportSection;